import { StorageClient, TrackingStorageClient, Value } from './types';
import {
  CustomDimensions,
  StorageTrackingData,
} from '../../third-party-tracking/types';
import {
  CUSTOM_DIMENSIONS_STORAGE_KEY,
  CUSTOM_PARAMS_STORAGE_KEY,
  DATALAYER_STORAGE_KEY,
  STORAGE_KEY,
  EVENTS_STORAGE_KEY,
  USER_PROPERTIES_STORAGE_KEY,
} from '../../third-party-tracking/resources/constants';
import {
  CustomDimensionsOrParams,
  CustomParams,
} from '../../third-party-tracking/google-analytics/ga4Types';
import {
  getCustomDimensionsFromParams,
  getCustomParamsFromDimensions,
  getUserPropertiesFromDimensions,
} from '../../third-party-tracking/google-analytics/ga4';
import {
  isParameterValueChanged,
  removeKeysFromObject,
} from '../../utils/object';
import {
  customMetrics,
  customParamsHitFlowScope,
  customParamsHitSearchScope,
  customParamsProduct,
  legacyDimensionsHitFlowScope,
  legacyDimensionsHitSearchScope,
  legacyDimensionsProduct,
  legacyMetricsHit,
} from '../../third-party-tracking/customDimensionsRepository/constants';
import { isFlowOrProductChanged, trackUserProperties } from './utils';
import { createConfigParametersRepository } from '../index';
import { parseJson } from '../../third-party-tracking/utils/objects';
import { createRuntimeState } from '../state';

export const createEmptyTrackingData = (
  visitId: string
): StorageTrackingData => ({
  visitId: visitId,
  lastUpdate: new Date().getTime(),
  [DATALAYER_STORAGE_KEY]: {},
  [CUSTOM_DIMENSIONS_STORAGE_KEY]: {},
  [CUSTOM_PARAMS_STORAGE_KEY]: {},
  [USER_PROPERTIES_STORAGE_KEY]: {},
  [EVENTS_STORAGE_KEY]: {},
});

export const createTrackingStorageClient = (
  storageClient: StorageClient
): TrackingStorageClient => {
  const storageClientPromise =
    storageClient.status === 'idle'
      ? storageClient.init()
      : Promise.resolve(storageClient);

  const listenChangeUserConsent = async (callback: () => void) => {
    const storageClient = await storageClientPromise;
    storageClient.listenChangeUserConsent(callback);
  };

  const setInStorageAsync = async <T>(
    type: 'sessionStorage' | 'localStorage',
    key: string,
    value: T
  ) => {
    const storageClient = await storageClientPromise;
    const storageRepository = storageClient.get(type);
    storageRepository.set(key, value);
  };

  const getFromStorageAsync = async <T>(
    type: 'sessionStorage' | 'localStorage',
    key: string,
    fallbackValue?: T
  ): Promise<T> => {
    const storageClient = await storageClientPromise;
    const storageRepository = storageClient.get(type);
    return storageRepository.get(key, fallbackValue);
  };

  /**
   * use only with technical cookies
   */
  const setCookie = (
    name: string,
    value: string | number,
    days: number,
    domain?: string
  ) => {
    const cookieRepository = storageClient.get('cookie');
    cookieRepository.set(name, value, days, domain);
  };

  /**
   * use only with technical cookies
   */
  const getCookie = (key: string) => {
    const cookieRepository = storageClient.get('cookie');
    return cookieRepository.get(key);
  };

  const cleanAndSetParams = <T extends Record<string, unknown>>(
    keysToRemove: (keyof T)[],
    originalParams: T,
    storageKey: string
  ) => {
    const cleanedParams: Partial<T> = removeKeysFromObject(
      keysToRemove,
      originalParams
    );
    update(storageKey, cleanedParams as CustomDimensionsOrParams);
  };

  const cleanStorageFromSearchScopeDimensions = (searches: string | number) => {
    const parametersInStorage = getTrackingStoredValue(
      CUSTOM_PARAMS_STORAGE_KEY
    ) as CustomParams;
    const dimensionsInStorage = getTrackingStoredValue(
      CUSTOM_DIMENSIONS_STORAGE_KEY
    ) as CustomDimensions;

    if (!isParameterValueChanged('searches', searches, parametersInStorage)) {
      return;
    }

    const hitAndProductScopeParameters = [
      ...customParamsProduct,
      ...customMetrics,
      ...customParamsHitSearchScope,
    ];
    cleanAndSetParams(
      hitAndProductScopeParameters,
      parametersInStorage,
      CUSTOM_PARAMS_STORAGE_KEY
    );

    const hitAndProductScopeDimensions = [
      ...legacyDimensionsHitSearchScope,
      ...legacyMetricsHit,
      ...legacyDimensionsProduct,
    ];
    cleanAndSetParams(
      hitAndProductScopeDimensions,
      dimensionsInStorage,
      CUSTOM_DIMENSIONS_STORAGE_KEY
    );
  };

  const cleanStorageFromFlowScopeDimensions = (screenName: string) => {
    const parametersInStorage = getTrackingStoredValue(
      CUSTOM_PARAMS_STORAGE_KEY
    ) as CustomParams;
    const dimensionsInStorage = getTrackingStoredValue(
      CUSTOM_DIMENSIONS_STORAGE_KEY
    ) as CustomDimensions;

    if (!isFlowOrProductChanged(screenName, parametersInStorage)) {
      return;
    }

    const hitAndProductScopeParameters = [
      ...customParamsProduct,
      ...customMetrics,
      ...customParamsHitSearchScope,
      ...customParamsHitFlowScope,
    ];
    cleanAndSetParams(
      hitAndProductScopeParameters,
      parametersInStorage,
      CUSTOM_PARAMS_STORAGE_KEY
    );

    const hitAndProductScopeDimensions = [
      ...legacyDimensionsProduct,
      ...legacyMetricsHit,
      ...legacyDimensionsHitSearchScope,
      ...legacyDimensionsHitFlowScope,
    ];
    cleanAndSetParams(
      hitAndProductScopeDimensions,
      dimensionsInStorage,
      CUSTOM_DIMENSIONS_STORAGE_KEY
    );
  };

  const filterDimensionsAndParams = (
    customDimensionsAndParams: CustomDimensionsOrParams
  ) => {
    const { searches, screen_name } = getCustomParamsFromDimensions(
      customDimensionsAndParams
    );
    if (!searches && !screen_name) {
      return;
    }
    if (searches) {
      cleanStorageFromSearchScopeDimensions(searches);
    }
    if (screen_name) {
      cleanStorageFromFlowScopeDimensions(screen_name);
    }
  };

  const updateCustomParamsAndDimensions = (
    customDimensionsAndParams: CustomDimensionsOrParams
  ) => {
    filterDimensionsAndParams(customDimensionsAndParams);
    updateCustomDimensions(customDimensionsAndParams);
    updateCustomParams(customDimensionsAndParams);
    updateUserProperties(customDimensionsAndParams);
  };

  const getTrackingStoredValue = (key: string) => {
    const storageRepository = storageClient.get('sessionStorage');
    return storageRepository.get(STORAGE_KEY)[key];
  };

  const updateCustomDimensions = (
    customDimensionsAndParams: CustomDimensionsOrParams
  ) => {
    const customDimensions = getCustomDimensionsFromParams({
      ...customDimensionsAndParams,
      market: null, // TODO: remove this when transition to GA4 is complete
    });
    if (!customDimensions || !Object.keys(customDimensions).length) {
      return;
    }
    update(CUSTOM_DIMENSIONS_STORAGE_KEY, (existingData = {}) => {
      return { ...existingData, ...customDimensions };
    });
  };

  const updateCustomParams = (
    customDimensionsAndParams: CustomDimensionsOrParams
  ) => {
    const customParams = getCustomParamsFromDimensions(
      customDimensionsAndParams
    );
    if (!customParams || !Object.keys(customParams).length) {
      return;
    }
    update(CUSTOM_PARAMS_STORAGE_KEY, (existingData = {}) => {
      return { ...existingData, ...customParams };
    });
  };

  const updateUserProperties = (
    customDimensionsAndParams: CustomDimensionsOrParams
  ) => {
    const userProps = getUserPropertiesFromDimensions(
      customDimensionsAndParams
    );
    if (!userProps || !Object.keys(userProps).length) {
      return;
    }
    trackUserProperties(userProps);
    update(USER_PROPERTIES_STORAGE_KEY, (existingData = {}) => {
      return { ...existingData, ...userProps };
    });
  };

  /**
   * use only with technical keys
   */
  const update = (key: string, value: Value) => {
    const sessionStorageRepository = storageClient.get('sessionStorage');
    const storedTrackingData: StorageTrackingData =
      sessionStorageRepository.get(STORAGE_KEY) ?? {};
    const nextValue =
      typeof value === 'function' ? value(storedTrackingData[key]) : value;
    sessionStorageRepository.set(STORAGE_KEY, {
      ...storedTrackingData,
      [key]: nextValue,
      lastUpdate: new Date().getTime(),
    });
  };

  const initThirdParty = (visitId: string) => {
    if (!visitId) {
      return;
    }
    const sessionStorageRepository = storageClient.get('sessionStorage');
    if (!sessionStorageRepository) {
      return;
    }
    const storedTrackingData: StorageTrackingData =
      sessionStorageRepository.get(STORAGE_KEY) ?? null;
    if (storedTrackingData?.visitId === visitId) {
      return;
    }
    let openerTrackingData: StorageTrackingData | null = null;
    try {
      openerTrackingData = parseJson(
        window.opener?.sessionStorage?.getItem(STORAGE_KEY)
      );
    } catch (_) {
      // opener access is restricted by CORS
    }
    if (openerTrackingData?.visitId === visitId) {
      sessionStorageRepository.set(STORAGE_KEY, openerTrackingData);
    } else {
      sessionStorageRepository.set(
        STORAGE_KEY,
        createEmptyTrackingData(visitId)
      );
    }
    const { setParameters } = createRuntimeState();
    setParameters({ isStorageInitialized: true });
  };

  /**
   * use only with technical keys
   */
  const isAllowed = (
    type: 'sessionStorage' | 'localStorage' | 'cookie',
    key: string
  ) => {
    const storageRepository = storageClient.get(type);
    return (storageRepository.get(key) as boolean) || false;
  };

  /**
   * use only with technical keys
   */
  const setInStorage = <T>(
    type: 'sessionStorage' | 'localStorage',
    key: string,
    value: T
  ) => {
    const sessionStorageRepository = storageClient.get(type);
    sessionStorageRepository.set(key, value);
  };

  /**
   * use only with technical keys
   */
  const getFromStorage = <T>(
    type: 'sessionStorage' | 'localStorage',
    key: string,
    fallbackValue?: T
  ): T => {
    const storageRepository = storageClient.get(type);
    return storageRepository.get(key, fallbackValue);
  };

  const removeFromStorage = (
    type: 'sessionStorage' | 'localStorage',
    key: string
  ) => {
    const storageRepository = storageClient.get(type);
    return storageRepository.delete(key);
  };

  const removeCookie = (key: string) => {
    const cookieRepository = storageClient.get('cookie');
    return cookieRepository.delete(key);
  };

  return {
    initThirdParty,
    isAllowed,
    getTrackingStoredValue,
    update,
    updateCustomParamsAndDimensions,
    getCookie,
    listenChangeUserConsent,
    setInStorage,
    getFromStorage,
    removeFromStorage,
    setCookie,
    setInStorageAsync,
    getFromStorageAsync,
    removeCookie,
    cleanStorageFromSearchScopeDimensions,
    cleanStorageFromFlowScopeDimensions,
  };
};

export const checkThirdPartyTrackingStorageInit = () => {
  const { getTrackingStorageClient, getGenericParameters } =
    createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  const { getRuntimeState } = createRuntimeState();
  const { isStorageInitialized } = getRuntimeState();
  if (isStorageInitialized) {
    return;
  }
  const genericParameters = getGenericParameters();
  storageClient?.initThirdParty(genericParameters?.visitID);
};
